import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Shield, AlertTriangle, Target, FileText, Users, CheckCircle, Plus, ExternalLink, FileSignature } from "lucide-react";
import { SharePointFileUpload } from "../ui/sharepoint-file-upload";
import DocumentListWithActions from "../documents/DocumentListWithActions";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "./PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

type FraudRiskLandingProps = {
  onSelect: (section: string) => void;
};

const categories: Array<{
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}> = [
  {
    id: "inquiries-management",
    title: "Inquiries of the management and others within the entity",
    description: "Conduct inquiries with management and other personnel regarding fraud risks.",
    icon: Users,
    accent: "from-blue-500/20 to-indigo-500/20",
  },
  {
    id: "planning-discussion",
    title: "Planning Discussion with the Client/Entity",
    description: "Hold planning discussions with the client/entity about fraud risks.",
    icon: Target,
    accent: "from-amber-500/20 to-orange-500/20",
  },
  {
    id: "fraud-risk-factors",
    title: "Fraud Risk Factors",
    description: "Identify and assess fraud risk factors.",
    icon: Shield,
    accent: "from-emerald-500/20 to-teal-500/20",
  },
  {
    id: "management-override",
    title: "Management Override of Controls",
    description: "Assess risks of management override of controls.",
    icon: AlertTriangle,
    accent: "from-purple-500/20 to-pink-500/20",
  },
  {
    id: "communication-management",
    title: "Communication with management and those charged with governance regarding matters of fraud",
    description: "Communicate fraud-related matters with management and governance.",
    icon: FileText,
    accent: "from-red-500/20 to-rose-500/20",
  },
];

const WORKSPACE_NAME = "Fraud Risk Assessment";

export default function FraudRiskLanding({ onSelect }: FraudRiskLandingProps) {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffsMap, setCardSignoffsMap] = useState<Record<string, MiscSignoffEntry[]>>({});
  const [docs, setDocs] = useState<Array<{ name: string; url: string; reference?: string; prep_so: any; rev_so: any; conc_so: any; eqr_so: any; partner_so: any }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const loadCardSignoffs = async () => {
    try {
      const data = await loadMiscSignoffs();
      const map: Record<string, MiscSignoffEntry[]> = {};
      categories.forEach((c) => {
        map[c.title] = getSignoffEntriesForCard(WORKSPACE_NAME, c.title, data.signoff);
      });
      setCardSignoffsMap(map);
    } catch {
      setCardSignoffsMap({});
    }
  };

  useEffect(() => {
    loadCardSignoffs();
  }, []);

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError("");
      // Use "Planning_FRA" to match the section field format used when linking files
      const sectionKey = "Planning_FRA";
      const res = await (window as any)?.sharePointAPI?.loadDocumentListBySection?.(sectionKey);
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load document list");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading document list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => { await loadDocs(); };
    run();
  }, []);

  const handleSubSectionClick = (subSectionId: string) => {
    // Navigate to the fraud-risk section with the specific sub-section
    onSelect(`fraud-risk-${subSectionId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Fraud Risk Assessment</h2>
          <p className="text-sm text-white/60">
            Choose a workflow to proceed. You can return here anytime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSignoffDialogOpen(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <FileSignature className="h-4 w-4 mr-1.5" />
            Sign off
          </Button>
          <Badge className="bg-white/10 text-white/80">Planning</Badge>
        </div>
      </div>

      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={categories.map((c) => c.title)}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((cat) => {
          const Icon = cat.icon;
          return (
            <Card
              key={cat.id}
              className="group cursor-pointer border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10"
              onClick={() => handleSubSectionClick(cat.id)}
            >
              <CardContent className="p-4">
                <div
                  className={`mb-4 inline-flex rounded-lg bg-gradient-to-br ${cat.accent} p-2`}
                >
                  <Icon className="h-5 w-5 text-white/80" />
                </div>
                <div className="mb-1 text-sm font-semibold">{cat.title}</div>
                <div className="mb-2 text-xs text-white/60">
                  {cat.description}
                </div>
                <PlanningSignoffTokens signoffs={cardSignoffsMap[cat.title]} />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 border-white/10 text-xs text-white/80 group-hover:bg-white/10 group-hover:text-white"
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Documents Section */}
      <DocumentListWithActions
        documents={docs}
        loading={loading}
        error={error}
        onRefresh={loadDocs}
        title="Document List"
        showUploadButton={true}
        uploadSectionList={["Planning_FRA_File"]}
        uploadFolderName="client"
        uploadMultiple={false}
        workspaceName="Fraud Risk"
        onNavigateToConfirmationLibrary={(w) => onSelect?.("confirmation-library:" + encodeURIComponent(w))}
        onNavigateToSamplingLibrary={(w) => onSelect?.("sampling-library:" + encodeURIComponent(w))}
      />
    </div>
  );
}
